import {
  TextFieldTester,
  BooleanTester,
  EnumAutocompleteTester,
  RadioTester,
  PaymentControlTester,
  AddressControlTester,
  UploadPanelControlTester,
  ProductSelectionControlTester,
  ProductCategoryControlTester,
  ProductRecommendationsControlTester,
  DigitalSignatureControlTester,
  PreviousProviderControlTester,
  SummaryControlTester,
  PdfSummaryControlTester,
  ConfirmationMessageControlTester,
  ActionBarControlTester,
  AuthControlTester,
  SecondaryActionBarControlTester,
  ConsentsControlTester,
  CustomBlockControlTester,
  MeterReadingControlTester,
  MeterReadingDynamicControlTester,
  ShopCartControlTester,
  DatePickerControlTester,
  NumberInputControlTester,
  InputCalculatorControlTester,
  AvailabilityCheckControlTester,
  MultichoiceControlTester,
  JourneyLauncherControlTester,
  EntityAttributeControlTester,
  EntityLookupControlTester,
  MainContentCartLayoutTester,
  MainLinearLayoutTester,
  GroupLayoutTester,
  LabelRendererTester,
  PersonalInformationControlTester,
  ContactControlTester,
  ImageControlTester,
  PvRoofPlannerControlTester,
  AccountControlTester,
  AppBlockControlTester,
  GridLayoutTester
} from '@epilot/journey-logic-commons'
import type { JsonFormsRendererRegistryEntry } from '@jsonforms/core'

import {
  StackLayout,
  TwoColumnsLayout,
  GridLayout,
  GroupLayout
} from '../../concorde-renderers/layouts'

import {
  SuspendedAddressControl,
  SuspendedActionBarControl,
  SuspendedAuthControl,
  SuspendedDatePickerControl,
  SuspendedAvailabilityCheckControl,
  MultichoiceControlRenderer,
  JourneyLauncherControl,
  SuspendedPersonalInformationControl,
  SuspendedImageControl,
  SuspendedBooleanControl,
  SuspendedNumberInputControl,
  SuspendedPaymentControl,
  SuspendedPreviousProviderControl,
  SuspendedParagraphControl,
  SuspendedProductSelectionControl,
  SuspendedTextFieldControl,
  SuspendedSecondaryActionBarControl,
  SuspendedPvRoofPlannerControl,
  SuspendedAutocompleteControl,
  SuspendedEnumButtonControl,
  SuspendedEnumRadioControl,
  SuspendedImageButtonControl,
  SuspendedUploadPanelControl,
  SuspendedMeterReadingControl,
  SuspendedMeterReadingDynamicControl,
  SuspendedDigitalSignatureControl,
  SuspendedConsentsControl,
  SuspendedPdfSummaryControl,
  SuspendedConfirmationControl,
  SuspendedCustomBlockControl,
  SuspendedInputCalculatorControl,
  SuspendedProductCategoryControl,
  SuspendedShopCartControl,
  SuspendedEntityAttributeControl,
  SuspendedEntityLookupControl,
  SuspendedSummaryControl,
  SuspendedAccountControl,
  SuspendedContactControl,
  SuspendedAppBlockControl,
  SuspendedProductRecommendationsControl
} from './controls'
import {
  EnumButtonControlTester,
  EnumImageButtonControlTester
} from './controls/SingleChoiceControl'

type ExtendedRenderersRegistry = (JsonFormsRendererRegistryEntry & {
  featureFlag?: boolean
})[]

export const getRegistry = (): ExtendedRenderersRegistry => {
  return [
    // controls
    { tester: TextFieldTester, renderer: SuspendedTextFieldControl },
    { tester: BooleanTester, renderer: SuspendedBooleanControl },
    { tester: EnumButtonControlTester, renderer: SuspendedEnumButtonControl },
    {
      tester: EnumImageButtonControlTester,
      renderer: SuspendedImageButtonControl
    },
    { tester: EnumAutocompleteTester, renderer: SuspendedAutocompleteControl },
    { tester: RadioTester, renderer: SuspendedEnumRadioControl },
    { tester: PaymentControlTester, renderer: SuspendedPaymentControl },
    { tester: AddressControlTester, renderer: SuspendedAddressControl },
    { tester: UploadPanelControlTester, renderer: SuspendedUploadPanelControl },
    {
      tester: ProductSelectionControlTester,
      renderer: SuspendedProductSelectionControl
    },
    {
      tester: ProductCategoryControlTester,
      renderer: SuspendedProductCategoryControl
    },
    {
      tester: ProductRecommendationsControlTester,
      renderer: SuspendedProductRecommendationsControl
    },
    {
      tester: DigitalSignatureControlTester,
      renderer: SuspendedDigitalSignatureControl
    },
    {
      tester: PreviousProviderControlTester,
      renderer: SuspendedPreviousProviderControl
    },
    { tester: SummaryControlTester, renderer: SuspendedSummaryControl },
    { tester: PdfSummaryControlTester, renderer: SuspendedPdfSummaryControl },
    {
      tester: ConfirmationMessageControlTester,
      renderer: SuspendedConfirmationControl
    },
    {
      tester: ActionBarControlTester,
      renderer: SuspendedActionBarControl
    },
    {
      tester: AuthControlTester,
      renderer: SuspendedAuthControl
    },
    {
      tester: SecondaryActionBarControlTester,
      renderer: SuspendedSecondaryActionBarControl
    },
    {
      tester: ConsentsControlTester,
      renderer: SuspendedConsentsControl
    },
    {
      tester: CustomBlockControlTester,
      renderer: SuspendedCustomBlockControl
    },
    {
      tester: MeterReadingControlTester,
      renderer: SuspendedMeterReadingControl
    },
    {
      tester: MeterReadingDynamicControlTester,
      renderer: SuspendedMeterReadingDynamicControl
    },
    {
      tester: ShopCartControlTester,
      renderer: SuspendedShopCartControl
    },
    {
      tester: DatePickerControlTester,
      renderer: SuspendedDatePickerControl
    },
    {
      tester: NumberInputControlTester,
      renderer: SuspendedNumberInputControl
    },
    {
      tester: InputCalculatorControlTester,
      renderer: SuspendedInputCalculatorControl
    },
    {
      tester: AvailabilityCheckControlTester,
      renderer: SuspendedAvailabilityCheckControl
    },
    {
      tester: MultichoiceControlTester,
      renderer: MultichoiceControlRenderer
    },
    {
      tester: JourneyLauncherControlTester,
      renderer: JourneyLauncherControl
    },
    {
      tester: EntityAttributeControlTester,
      renderer: SuspendedEntityAttributeControl
    },
    {
      tester: EntityLookupControlTester,
      renderer: SuspendedEntityLookupControl
    },
    {
      tester: PersonalInformationControlTester,
      renderer: SuspendedPersonalInformationControl
    },
    { tester: ContactControlTester, renderer: SuspendedContactControl },
    { tester: AccountControlTester, renderer: SuspendedAccountControl },
    {
      tester: AppBlockControlTester,
      renderer: SuspendedAppBlockControl
    },

    // layouts
    { tester: MainContentCartLayoutTester, renderer: TwoColumnsLayout },
    { tester: MainLinearLayoutTester, renderer: StackLayout },
    { tester: GroupLayoutTester, renderer: GroupLayout },
    { tester: GridLayoutTester, renderer: GridLayout },

    // display
    { tester: LabelRendererTester, renderer: SuspendedParagraphControl },
    { tester: ImageControlTester, renderer: SuspendedImageControl },
    {
      tester: PvRoofPlannerControlTester,
      renderer: SuspendedPvRoofPlannerControl
    }
  ]
}
